
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const { login, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    console.log("Login component mounted");
    console.log("Login: Auth loading:", authLoading, "isAuthenticated:", isAuthenticated);
  }, []);

  // Redirect to project selector if already authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      console.log("Login: User already authenticated, redirecting to projects");
      navigate("/projects-selector", { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Pre-fill demo credentials for easier testing
  useEffect(() => {
    setEmail("john@example.com");
    setPassword("password");
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginError(null);

    try {
      console.log("Login: Attempting login with email:", email);
      await login(email, password);
      console.log("Login: Login successful");
      toast.success("Login successful!");
      navigate("/projects-selector", { replace: true });
    } catch (error) {
      console.error("Login: Login failed", error);
      setLoginError((error as Error).message || "Login failed. Please try again.");
      toast.error("Invalid email or password");
    } finally {
      setIsLoading(false);
    }
  };

  // If auth is still loading, show a loading indicator
  if (authLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <Loader2 className="h-10 w-10 text-primary animate-spin mb-4" />
        <p className="text-gray-500">Checking authentication...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
      <div className="w-full max-w-md p-8 space-y-8 bg-white rounded-lg shadow-lg">
        <div className="text-center">
          <h1 className="text-3xl font-bold">
            <span className="text-primary">Strata</span>
            <span className="text-secondary">fold</span>
          </h1>
          <p className="mt-2 text-gray-600">
            Every Layer. Every Milestone. One Platform
          </p>
        </div>

        {loginError && (
          <Alert variant="destructive">
            <AlertDescription>{loginError}</AlertDescription>
          </Alert>
        )}

        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <a
                href="#"
                className="text-xs text-primary hover:text-primary/90"
              >
                Forgot password?
              </a>
            </div>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing in...
              </>
            ) : (
              "Sign in"
            )}
          </Button>
        </form>

        <div className="text-center text-sm text-gray-500 mt-4">
          <p>
            Demo credentials:
            <br />
            Email: john@example.com
            <br />
            Password: password
          </p>
        </div>
      </div>
    </div>
  );
}
